import React, { useState, useEffect, useContext } from 'react'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { useMutation } from '@apollo/client'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import {
  Input,
  Select,
  Steps,
  Row,
  Col,
  Divider,
  Typography,
  Form as AntdForm,
  message,
} from 'antd'
import EmailLogin from 'components/auth/EmailLogin'
import SocialLink from 'components/auth/SocialLink'
import Button from 'components/shared/button'
import authContext from 'contexts/authStatus'
import { EditorState, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import { Formik, Field, Form } from 'formik'
import { CreateBlogDocument } from 'graphql/generated/types'

const steps = {
  'Login/Register': 0,
  'Create Post': 1,
  Done: 2,
}

const Editor = dynamic(
  () => import('react-draft-wysiwyg').then((mod) => mod.Editor),
  { ssr: false }
)

const { Step } = Steps
const { Option } = Select
const { Paragraph, Title } = Typography

export default function NewPost(props) {
  const { categories } = props
  const router = useRouter()
  const [current, setCurrent] = useState(steps['Login/Register'])
  const [currentUserToken, setCurrentUserToken] = useState(null)
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const [contentErr, setContentErr] = useState(false)
  const [countryId, setCountryId] = useState(null)
  // @ts-ignore
  const { setLogin } = useContext(authContext)
  const { country_slug } = router.query
  useEffect(() => {
    if (localStorage.authToken) {
      setCurrentUserToken(localStorage.authToken)
      setCurrent(steps['Create Post'])
    }

    switch (country_slug) {
      case 'ca':
        setCountryId(2)
        break
      case 'us':
        setCountryId(4)
        break
      default:
        setCountryId(1)
    }
  }, [])

  const [AddFaq] = useMutation(CreateBlogDocument, {
    context: {
      headers: {
        Authorization: `Bearer ${currentUserToken}`,
      },
    },
  })

  const onEditorStateChange = (editorData) => {
    setEditorState(editorData)
    const checkData = editorData.getCurrentContent().hasText()
    if (!checkData) {
      setContentErr(true)
    } else {
      setContentErr(false)
    }
  }

  const submitQuery = (values, editorData) => {
    AddFaq({
      variables: {
        data: {
          slug: values.title,
          title: values.title,
          short_description: values.short_description,
          user: localStorage.userId,
          tags: values.tags,
          article_type: 'blog',
          description: editorData,
          country: countryId,
        },
      },
    })
      .then(() => {
        message.info('Your post is submitted for Review')
        setCurrent(steps.Done)
      })
      .catch((e) => {
        message.error(e.message)
      })
  }

  const sumbitContentCallback = async () => {
    setLogin(true)
    await setCurrentUserToken(localStorage.authToken)
    message.info('Successfully Logged In!')
    router.push(`/${[country_slug]}/create-post`)
  }

  return (
    <Row
      className="page-wrapper page"
      style={{ justifyContent: 'center', display: 'flex' }}
    >
      <Col xs={24} lg={12}>
        <Steps progressDot current={current}>
          <Step
            title="Login/Register"
            description="please login/register to post your blog"
          />
          <Step
            title="Create your Post"
            description="Please type your investment related post."
          />

          <Step title="Done" />
        </Steps>
        {current === steps['Login/Register'] && (
          <Row className="authPage">
            <Row>
              <Col span="24">
                <Row className="headerDescription">
                  <Col span="24">
                    <Title level={2}>Login to your Account.</Title>
                    <Paragraph>Please enter your details to login.</Paragraph>
                  </Col>
                </Row>
                <Row className="formContainer" style={{ marginTop: '32px' }}>
                  <Col span="24">
                    <Row className="row">
                      <Col span="24">
                        <SocialLink
                        // callbackbeforeRedirect={() => sumbitContentCallback()}
                        />
                        <Divider>or</Divider>
                        <EmailLogin
                          callbackbeforeRedirect={() => sumbitContentCallback()}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Row>
        )}
        {current === steps['Create Post'] && (
          <div style={{ flexDirection: 'column', marginTop: '40px' }}>
            <Formik
              initialValues={{
                title: '',
                short_description: '',
                tags: [],
                content: EditorState.createEmpty(),
              }}
              validate={() => {
                const errors = {}
                return errors
              }}
              onSubmit={(values, actions) => {
                const checkData = editorState?.getCurrentContent()?.hasText()
                if (checkData) {
                  const contentState = editorState.getCurrentContent()
                  const rawContentState = draftToHtml(
                    convertToRaw(contentState)
                  )
                  const editorData = JSON.stringify(rawContentState)
                  submitQuery(values, editorData)
                } else {
                  setContentErr(true)
                }
                actions.setSubmitting(false)
              }}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form>
                  <AntdForm.Item
                    label=""
                    name="title"
                    rules={[
                      {
                        required: true,
                        message: 'Title is required',
                      },
                    ]}
                  >
                    <Field
                      name="title"
                      validate={(value) =>
                        !value || value.length === 0 ? 'Required' : undefined
                      }
                    >
                      {({ field }) => (
                        <div>
                          Title:
                          <Input id="title" placeholder="Title" {...field} />
                        </div>
                      )}
                    </Field>
                    {errors.title && touched.title && (
                      <div style={{ color: 'red' }}>{errors.title}</div>
                    )}
                  </AntdForm.Item>
                  <AntdForm.Item
                    label=""
                    name="short_description"
                    rules={[
                      {
                        required: true,
                        message: 'Short Description is required',
                      },
                    ]}
                  >
                    <Field
                      name="short_description"
                      validate={(value) =>
                        !value || value.length === 0 ? 'Required' : undefined
                      }
                    >
                      {({ field }) => (
                        <div>
                          Description:
                          <Input
                            id="short_description"
                            placeholder="Short Description"
                            {...field}
                          />
                        </div>
                      )}
                    </Field>
                    {errors.short_description && touched.short_description && (
                      <div style={{ color: 'red' }}>
                        {errors.short_description}
                      </div>
                    )}
                  </AntdForm.Item>
                  <AntdForm.Item
                    label=""
                    name="tags"
                    rules={[
                      {
                        required: true,
                        message: 'Please select an option',
                      },
                    ]}
                  >
                    <Field
                      name="tags"
                      validate={(value) =>
                        !value || value.length === 0 ? 'Required' : undefined
                      }
                    >
                      {({ field, form }) => (
                        <div>
                          Tags:
                          <br />
                          <Select
                            mode="multiple"
                            id="tags"
                            placeholder="Keywords to be tagged"
                            style={{ width: 200 }}
                            rules={[
                              {
                                required: true,
                                message: 'Please select an option',
                              },
                            ]}
                            {...field}
                            onChange={(value) =>
                              form.setFieldValue('tags', value)
                            }
                            onBlur={() => form.setFieldTouched('tags', true)}
                          >
                            {categories.map((category) => (
                              <Option
                                key={category.id}
                                value={category.attributes.name}
                              >
                                {category.attributes.name}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      )}
                    </Field>
                    {errors.tags && touched.tags && (
                      <div style={{ color: 'red' }}>Required</div>
                    )}
                  </AntdForm.Item>
                  <AntdForm.Item label="Content" name="content">
                    <Editor
                      editorState={editorState}
                      editorStyle={{ border: '1px solid', height: '250px' }}
                      toolbarClassName="toolbar-class"
                      wrapperClassName="wrapper-class"
                      editorClassName="editor-class"
                      onEditorStateChange={onEditorStateChange}
                      toolbar={{
                        options: [
                          'inline',
                          'blockType',
                          'fontSize',
                          'fontFamily',
                          'list',
                          'textAlign',
                          'colorPicker',
                          'link',
                          'embedded',
                          'emoji',
                          'image',
                          'history',
                        ],
                        inline: { inDropdown: true },
                        list: { inDropdown: true },
                        textAlign: { inDropdown: true },
                        link: { inDropdown: true },
                        history: { inDropdown: true },
                        image: {
                          urlEnabled: true,
                          uploadEnabled: true,
                          previewImage: true,
                          alt: { present: false, mandatory: false },
                        },
                      }}
                    />
                    <div style={{ color: 'red' }}>
                      {contentErr ? 'Required' : ''}
                    </div>
                  </AntdForm.Item>
                  <br />
                  <br />
                  <Button
                    type="primary"
                    htmlType="submit"
                    source="submit-query"
                    loading={isSubmitting}
                  >
                    Submit for Review
                  </Button>
                </Form>
              )}
            </Formik>
          </div>
        )}

        {current === steps.Done && (
          <Row className="authPage">
            <Col>
              <div>Expert users on our site will approve your post soon</div>
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  )
}
